/**
 * Attachments, in both directions.
 *
 * What the Gateway actually accepts is narrower than what the schema suggests:
 * `ChatSendParams.attachments` takes any `mimeType`, but the chat pipeline
 * sniffs each payload and **drops everything that is not an image**, without an
 * error (openclaw#48123; the official Control UI picker is image-only for the
 * same reason, openclaw#69447). A silent drop is the worst possible outcome for
 * a messenger, so this module refuses non-images up front and says why.
 *
 * Payloads travel as a `data:` URL in `content` — the shape the Control UI
 * sends and the Gateway's parser explicitly strips (openclaw#99213).
 *
 * Sizes are bounded twice over: `policy.attachments` carries the decoded
 * ceilings, and the whole frame still has to fit `policy.maxPayload`, which
 * base64 inflates by a third. Oversized images are re-encoded rather than
 * rejected — a phone photo is routinely larger than the image ceiling, and a
 * messenger that refuses the camera roll is not a messenger.
 */

export type OutgoingAttachment = {
  /** Local id, for the draft strip and the optimistic bubble. */
  id: string;
  type: "image";
  mimeType: string;
  fileName: string;
  /** `data:<mime>;base64,<payload>` — what goes on the wire. */
  content: string;
  sizeBytes: number;
  width?: number;
  height?: number;
};

/** An attachment as rendered in the transcript, from either direction. */
export type ChatAttachment = {
  id: string;
  kind: "image" | "file";
  mimeType?: string;
  fileName?: string;
  /** Bytes already in hand — an inline block, or one we just sent. */
  dataUrl?: string;
  /** Bytes that have to be fetched with `artifacts.download`. */
  artifactId?: string;
  /** A Gateway-origin URL, when the artifact is served rather than inlined. */
  url?: string;
  sizeBytes?: number;
  width?: number;
  height?: number;
  alt?: string;
};

export type AttachmentLimits = {
  /** Decoded ceiling for images (`policy.attachments.maxImageBytes`). */
  maxImageBytes: number;
  /** Decoded ceiling for anything else (`policy.attachments.maxBytes`). */
  maxBytes: number;
  /** Frame ceiling for the whole request (`policy.maxPayload`). */
  maxPayload: number;
};

/**
 * Used until the handshake says otherwise. The Gateway defaults are 20 MB per
 * attachment, images capped at the 6 MB agent-hydration limit, in a 25 MiB
 * frame.
 */
export const DEFAULT_LIMITS: AttachmentLimits = {
  maxImageBytes: 6 * 1024 * 1024,
  maxBytes: 20 * 1024 * 1024,
  maxPayload: 25 * 1024 * 1024,
};

export function readLimits(policy: unknown): AttachmentLimits {
  const source = policy as
    | { maxPayload?: number; attachments?: { maxBytes?: number; maxImageBytes?: number } }
    | undefined;
  const positive = (value: unknown, fallback: number) =>
    typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
  return {
    maxImageBytes: positive(source?.attachments?.maxImageBytes, DEFAULT_LIMITS.maxImageBytes),
    maxBytes: positive(source?.attachments?.maxBytes, DEFAULT_LIMITS.maxBytes),
    maxPayload: positive(source?.maxPayload, DEFAULT_LIMITS.maxPayload),
  };
}

export class AttachmentError extends Error {}

/** Longest edge an image is scaled down to before re-encoding. */
const MAX_EDGE_PX = 2048;
/** Below this, an image is sent untouched: re-encoding a screenshot blurs text. */
const REENCODE_ABOVE_BYTES = 1_500_000;
const JPEG_QUALITY = [0.85, 0.7, 0.55];

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

/** Bytes a `data:` URL costs on the wire, including the base64 inflation. */
export function wireSize(attachment: OutgoingAttachment): number {
  return attachment.content.length;
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new AttachmentError("Die Datei konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(
        new AttachmentError(
          "The browser cannot open this image format — try again as JPEG or PNG.",
        ),
      );
    image.src = dataUrl;
  });
}

function encode(canvas: HTMLCanvasElement, quality: number): string {
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Turns a picked file into something the Gateway will accept.
 *
 * Scales past `MAX_EDGE_PX` down and re-encodes as JPEG until the payload fits
 * the image ceiling. Small files keep their original bytes and format, so a
 * pasted screenshot stays a crisp PNG.
 */
export async function prepareImage(
  file: File,
  limits: AttachmentLimits,
  id: string,
): Promise<OutgoingAttachment> {
  if (!isImageFile(file)) {
    throw new AttachmentError(
      `“${file.name}” is not an image. The Gateway silently discards everything but images, so cllw does not take it in the first place.`,
    );
  }

  const original = await readAsDataUrl(file);
  const fileName = file.name || "bild.png";

  if (file.size <= REENCODE_ABOVE_BYTES && file.size <= limits.maxImageBytes) {
    const { width, height } = await loadImage(original).then(
      (image) => ({ width: image.naturalWidth, height: image.naturalHeight }),
      () => ({ width: undefined, height: undefined }),
    );
    return {
      id,
      type: "image",
      mimeType: file.type,
      fileName,
      content: original,
      sizeBytes: file.size,
      ...(width ? { width, height } : {}),
    };
  }

  const image = await loadImage(original);
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new AttachmentError("Das Bild konnte nicht verkleinert werden.");
  }
  context.drawImage(image, 0, 0, width, height);

  let content = "";
  for (const quality of JPEG_QUALITY) {
    content = encode(canvas, quality);
    // The ceiling is on decoded bytes; base64 carries 3 bytes per 4 characters.
    if (decodedSize(content) <= limits.maxImageBytes) {
      break;
    }
  }
  if (decodedSize(content) > limits.maxImageBytes) {
    throw new AttachmentError(
      `“${fileName}” stays above the ${formatBytes(limits.maxImageBytes)} limit even after shrinking.`,
    );
  }

  return {
    id,
    type: "image",
    mimeType: "image/jpeg",
    fileName: fileName.replace(/\.[^.]+$/, "") + ".jpg",
    content,
    sizeBytes: decodedSize(content),
    width,
    height,
  };
}

/** Decoded byte count of a `data:` URL, from its base64 length. */
export function decodedSize(dataUrl: string): number {
  const comma = dataUrl.indexOf(",");
  const payload = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

/**
 * Guards the frame itself. The Gateway closes a connection that overruns
 * `maxPayload`, so this has to hold before the send, not after.
 */
export function fitsPayload(
  attachments: readonly OutgoingAttachment[],
  messageLength: number,
  limits: AttachmentLimits,
): boolean {
  const total = attachments.reduce((sum, item) => sum + wireSize(item), messageLength);
  // Leaves room for the envelope: method name, session key, ids.
  return total <= limits.maxPayload * 0.95;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
